import React, {useMemo, useState} from "react";
import {useSelector} from "react-redux";
import TableWrapper from "../Table/tableWrapper";
import PlanUpgradeModal from "./planUpgradeModal";
import LoadingSpinner from "../CommonComponents/loadingSpinner";
import { createTableHeadersAndData, genericSorter, getFullDate } from "../../Util/TableHelpers/commonHelper";
import DropdownMenuList from "./dropdownMenuList";
import { isLastMonth } from "../../Util/filter";
import { plansSelector } from "../../Reducers/plansReducer";
import PlanBadge from "../CommonComponents/planBadge";

const NoAccountsText = "You do not have any accounts";

const columnConfigs = [
    {
        id: "index",
        name: "",
        dataFunc: (_, index) => `${index + 1}.`
    },
    {
        id: "companyName",
        name: "Business Name",
        dataFunc: ({account}) => account.companyName || "Unknown"
    },
    {
        id: "adminName",
        name: "Admin Name",
        dataFunc: ({account}) => account.adminName || "Unknown"
    },
    {
        id: "adminEmail",
        name: "Admin Email",
        dataFunc: ({account}) => account.email || "Unknown"
    },
    {
        id: "phoneNumber",
        name: "Phone Number",
        dataFunc: ({account}) => account.phoneNumber || "Unknown"
    },
    {
        id: "currentPlan",
        name: "Current Plan",
        dataFunc: ({account}) => <PlanBadge planRaw={account.currentPlanRaw} />
    },
    {
        id: "country",
        name: "Country",
        dataFunc: ({account}) => account.country || "Unknown"
    },
    {
        id: "lastMonthStatus",
        name: "Last Month Status",  
        dataFunc: ({account, plans, lastMonthBilling}) => {
            if (!plans.isPaid(account.currentPlanRaw)) return "N/A";
            if (lastMonthBilling === null) return "N/A";
            return <div className={lastMonthBilling?.isPaid ? "green-button" : "red-button"}>
                    {lastMonthBilling?.isPaid ? "Paid" : "Unpaid"}
                </div>;
        }
    },
    {
        id: "memberSince",
        name: "Member Since",
        dataFunc: ({account}) => getFullDate(account.memberSince) || "Unknown"
    },
    {
        id: "planMenu",
        name: "",
        dataFunc: ({account, handleUpgradeBtnClick}) => 
                <DropdownMenuList
                    selectedAdminId={account.dispatcherId} 
                    selectedCompanyId={account.companyId} 
                    handleUpgradeBtnClick={handleUpgradeBtnClick}
                />
    }
];

const getLastMonthBillingData = (billingDataObj) => {
    if (billingDataObj === null || billingDataObj === undefined) return null;

    const lastMonthBillingData = Object.entries(billingDataObj?.billingFormattedObj)
        .find(([key, value]) => isLastMonth(key))?.[1]?.billingInfos ?? null;

    if (lastMonthBillingData === null) return null;

    const lastMonthBillingMap = lastMonthBillingData?.reduce((acc, curr) => {
        acc[curr.companyId] = curr;
        return acc;
    }, {});

    return lastMonthBillingMap;
}

const AccountsTableSection = () => {
    const partnerAccountObj = useSelector(state => state.partnerAccounts);
    const partnerDetails = useSelector(state => state.partnerDetails);
    const billingDataObj = useSelector(state => state.partnerBillingData);
    const plansObj = useSelector(plansSelector);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);

    const lastMonthBillingMap = getLastMonthBillingData(billingDataObj);

    const tableInput = useMemo(() => {
        if (partnerAccountObj === null) return [];

        const accounts = partnerAccountObj.getAccountsArray();
      
        let input = accounts.map((account) => ({
            account: account,
            plans: plansObj,
            lastMonthBilling: lastMonthBillingMap?.[account.companyId] ?? null,
            handleUpgradeBtnClick: setSelectedCompanyId,
        }));

        input = input.sort((a, b) => {
            const aKey = plansObj.getOrderKey(a.account.currentPlanRaw);
            const bKey = plansObj.getOrderKey(b.account.currentPlanRaw);
            return genericSorter(aKey, bKey);
        });

        return input;
    }, [partnerAccountObj, plansObj, lastMonthBillingMap, setSelectedCompanyId]);

    if (partnerAccountObj === null || partnerDetails === null)
        return <LoadingSpinner target={'mainContainer'}/>;

    let processedColumnConfigs = partnerDetails.isPremiumOrResellerPartner() ? 
        columnConfigs : columnConfigs.filter(config => config.id !== "planMenu");
    if (lastMonthBillingMap === null) {
        processedColumnConfigs = processedColumnConfigs.filter(config => config.id !== "lastMonthStatus");
    }

    const {headers, data} = createTableHeadersAndData(tableInput, processedColumnConfigs);

    return (
        <div className="row mt-5 min-width-md-parent table-container">
            <TableWrapper 
                tableHeaderArr={headers} 
                tableBodyArr={data} 
                errorText={NoAccountsText}
            />
            {partnerDetails.isPremiumOrResellerPartner() && 
                <PlanUpgradeModal selectedCompanyId={selectedCompanyId}/>}
        </div>
    )
}

export default AccountsTableSection;
